import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export type AttendanceStatus = 'present' | 'remote' | 'holiday' | 'absent';

export interface AttendanceRecord {
  id: string;
  userId: string;
  workDate: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  breakMinutes: number;
  status: AttendanceStatus;
  note: string | null;
  workCountry: string | null;
  workedMinutes: number | null;
  overtimeMinutes: number | null;
}

export interface OvertimeViolation { rule: string; message: string; severity?: string }

export interface AttendanceMonth {
  year: number;
  month: number;
  records: AttendanceRecord[];
  summary: {
    daysWorked: number;
    totalWorkedHours: number;
    overtimeHours: number;
    violations: OvertimeViolation[];
  };
}

export function useTodayAttendance() {
  return useQuery({
    queryKey: ['attendance', 'today'],
    queryFn: async () => {
      const r = await api.get<{ date: string; record: AttendanceRecord | null }>('/api/attendance/today');
      return r.data;
    },
  });
}

export function useMyAttendance(year: number, month: number) {
  return useQuery({
    queryKey: ['attendance', 'month', year, month],
    queryFn: async () => {
      const r = await api.get<AttendanceMonth>('/api/attendance/me', { params: { year, month } });
      return r.data;
    },
  });
}

export function useClockIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input?: { status?: AttendanceStatus; workCountry?: string }) => {
      const r = await api.post<AttendanceRecord>('/api/attendance/clock-in', { status: input?.status, workCountry: input?.workCountry || undefined });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance'] }),
  });
}

export function useClockOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (breakMinutes?: number) => {
      const r = await api.post<AttendanceRecord>('/api/attendance/clock-out', { breakMinutes });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance'] }),
  });
}
